import { useAlert } from 'components/alert'
import { useSession } from 'components/auth/useSession'
import { useErrorHandler } from 'components/error'
import { CIDADAO_PATH } from 'components/layout/SideMenu/SideMenuItems'
import { confirm } from 'components/modals/confirm'
import { useExcluirCidadaoMutation, useReativarCidadaoMutation } from 'graphql/hooks.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import Permissions from 'types/Permissions'
import { CidadaoUrlParams } from 'view/cidadao/types/cidadao-urlParams'
import { AutorizacaoAbsorventesListaCidadaoModal } from 'view/dignidade-menstrual/AutorizacaoAbsorventesListaCidadaoModal'

import { useNavigateToCidadaoCallbackUrl } from '../../hooks/useNavigateToCidadaoCallbackUrl'
import { useNeedLoginGovBrToCadsus } from '../../hooks/useNeedLoginGovBrToCadsus'
import { CidadaoListingFormModel } from '../types/model-pesquisarCidadao'
import { CidadaoAldeadoListingItemView } from './CidadaoAldeadoListingItemView'
import { CidadaoListingItemView } from './CidadaoListingItemView'

interface CidadaoListingItemProps {
  data: CidadaoListingFormModel
  refetchLocal(): void
  cidadaoUrlParams: CidadaoUrlParams
  isAccessTokenGovBrAlive: boolean
  isConfiguracaoPDMAtiva: boolean
}

export const CidadaoListingItem = (props: CidadaoListingItemProps) => {
  const {
    data,
    cidadaoUrlParams: { callbackUrl },
    refetchLocal,
    isAccessTokenGovBrAlive,
    isConfiguracaoPDMAtiva,
  } = props
  const { analytics } = useFirebase()

  const [modalAutorizacaoAbsorventesOpen, setModalAutorizacaoAbsorventesOpen] = useState<boolean>(false)
  const { hasAuthorization } = useSession()
  const [excluirCidadao] = useExcluirCidadaoMutation()
  const [reativarCidadao] = useReativarCidadaoMutation()
  const externalAccess = !!callbackUrl

  const alert = useAlert()
  const handleRejection = useErrorHandler()
  const location = useLocation()
  const history = useHistory()

  const navigateToCidadaoCallbackUrl = useNavigateToCidadaoCallbackUrl()

  const telefone = data.telefoneCelular
    ? data.telefoneCelular
    : data.telefoneResidencial
    ? data.telefoneResidencial
    : null

  const cadastrarEEditar = hasAuthorization(Permissions.gestaoDeCadastros.visualizarCadastroIndividual.cadastrarEEditar)
  const excluir = hasAuthorization(Permissions.gestaoDeCadastros.visualizarCadastroIndividual.excluir)
  const inativar = hasAuthorization(Permissions.gestaoDeCadastros.visualizarCadastroIndividual.inativar)
  const visualizarAgenda = hasAuthorization(Permissions.visualizarAgenda)
  const gerarAutorizacaoAbsorventes = hasAuthorization(
    Permissions.gestaoDeCadastros.gerarAutorizacaoParaRetiradaDeAbsorventes
  )

  const hasAnyPermission = cadastrarEEditar || excluir || inativar || visualizarAgenda || gerarAutorizacaoAbsorventes

  const onExcluirClick = () => {
    confirm({
      title: `Deseja excluir o cadastro individual?`,
      confirmLabel: 'Excluir',
      type: 'danger',
      onConfirm: () => {
        return excluirCidadao({ variables: { cidadaoId: data.id } })
          .then(() => {
            alert('success', 'Cadastro individual excluído.')
            refetchLocal()
          })
          .catch(handleRejection)
      },
    })()
  }

  const onVisualizarClick = () => {
    history.push(`${CIDADAO_PATH}/${data.id}${location.search}`)
  }

  const { needLoginGovBr: deveAparecerModalGovBr } = useNeedLoginGovBrToCadsus()

  const [modalInformarLoginGovBrOpen, setModalInformarLoginGovBrOpen] = useState(false)

  const continueEditarCidadao = () => {
    history.push(`${CIDADAO_PATH}/${data.id}/edit${location.search}`)
  }

  const onEditarClick = () => {
    if (deveAparecerModalGovBr) {
      setModalInformarLoginGovBrOpen(true)
    } else {
      continueEditarCidadao()
    }
  }

  const onReativarClick = () => {
    confirm({
      title: `Deseja reativar o cadastro individual?`,
      confirmLabel: 'Reativar',
      type: 'primary',
      onConfirm: () => {
        return reativarCidadao({ variables: { cidadaoId: data.id } })
          .then(() => {
            alert('success', 'Cadastro individual reativado.')
            refetchLocal()
          })
          .catch(handleRejection)
      },
    })()
  }

  const handleVerAgendamentosClick = () => {
    history.push(`${CIDADAO_PATH}/${data.id}/agendamentos`)
  }

  const handleClickGerarAutorizacaoAbsorventes = () => {
    analytics.logEvent('gerar_autorizacao_absorventes_cidadao')
    setModalAutorizacaoAbsorventesOpen(true)
  }
  return (
    <>
      <AutorizacaoAbsorventesListaCidadaoModal
        open={modalAutorizacaoAbsorventesOpen}
        cidadaoId={data.id}
        onClose={() => setModalAutorizacaoAbsorventesOpen(false)}
      />
      {data.cidadaoAldeado ? (
        <CidadaoAldeadoListingItemView
          data={data}
          telefone={telefone}
          externalAccess={externalAccess}
          hasAnyPermission={hasAnyPermission}
          cadastrarEEditar={cadastrarEEditar}
          inativar={inativar}
          excluir={excluir}
          isAccessTokenGovBrAlive={isAccessTokenGovBrAlive}
          isConfiguracaoPDMAtiva={isConfiguracaoPDMAtiva}
          visualizarAgenda={visualizarAgenda}
          onExcluirClick={onExcluirClick}
          onVisualizarClick={onVisualizarClick}
          onEditarClick={onEditarClick}
          onReativarClick={onReativarClick}
          handleVerAgendamentosClick={handleVerAgendamentosClick}
          navigateToCidadaoCallbackUrl={navigateToCidadaoCallbackUrl}
          modalInformarLoginGovBrOpen={modalInformarLoginGovBrOpen}
          setModalInformarLoginGovBrOpen={setModalInformarLoginGovBrOpen}
          continueEditarCidadao={continueEditarCidadao}
          onClickGerarAutorizacaoAbsorventes={handleClickGerarAutorizacaoAbsorventes}
        />
      ) : (
        <CidadaoListingItemView
          data={data}
          telefone={telefone}
          externalAccess={externalAccess}
          hasAnyPermission={hasAnyPermission}
          cadastrarEEditar={cadastrarEEditar}
          inativar={inativar}
          excluir={excluir}
          isAccessTokenGovBrAlive={isAccessTokenGovBrAlive}
          isConfiguracaoPDMAtiva={isConfiguracaoPDMAtiva}
          visualizarAgenda={visualizarAgenda}
          onExcluirClick={onExcluirClick}
          onVisualizarClick={onVisualizarClick}
          onEditarClick={onEditarClick}
          onReativarClick={onReativarClick}
          handleVerAgendamentosClick={handleVerAgendamentosClick}
          navigateToCidadaoCallbackUrl={navigateToCidadaoCallbackUrl}
          modalInformarLoginGovBrOpen={modalInformarLoginGovBrOpen}
          setModalInformarLoginGovBrOpen={setModalInformarLoginGovBrOpen}
          continueEditarCidadao={continueEditarCidadao}
          onClickGerarAutorizacaoAbsorventes={handleClickGerarAutorizacaoAbsorventes}
        />
      )}
    </>
  )
}
